import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { useImpersonation } from 'dashboard/composables/useImpersonation';
import { useCallsStore } from 'dashboard/stores/calls';
import {
  applyOutboundAnswer,
  armOutboundRecorder,
  handleWhatsappRemoteEnd,
  isLocalWhatsappCall,
} from 'dashboard/composables/useWhatsappCallSession';
import { VOICE_CALL_PROVIDERS } from 'dashboard/helper/inbox';
import { markCallDismissed, isLocalCall } from 'dashboard/helper/voice';
import { VOICE_CALL_DIRECTION } from 'dashboard/components-next/message/constants';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { getConversationIdFromRouteParams } from 'dashboard/helper/routeHelpers';
import { getConversationListQuery } from './ReconnectService';

const { isImpersonating } = useImpersonation();
const UNREAD_COUNTS_REFETCH_THROTTLE_MS = 5000;
const FILTERED_UNREAD_COUNTS_REFRESH_RETRY_MS = 30000;
const FILTERED_UNREAD_COUNTS_REFRESH_RETRY_JITTER_MS = 15000;
const MENTION_UNREAD_COUNTS_REFETCH_DELAY_MS =
  UNREAD_COUNTS_REFETCH_THROTTLE_MS;
const getFilteredUnreadCountsRefreshRetryDelay = () =>
  FILTERED_UNREAD_COUNTS_REFRESH_RETRY_MS +
  Math.random() * FILTERED_UNREAD_COUNTS_REFRESH_RETRY_JITTER_MS;

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.lastUnreadCountsFetchAt = null;
    this.unreadCountsFetchTimer = null;
    this.mentionUnreadCountsFetchTimer = null;
    this.mentionUnreadCountsRetryTimer = null;
    this.filteredUnreadCountsRetryTimer = null;
    this.revokedInboxIds = new Set();
    this.accountId = app.$store.getters.getCurrentAccountId;
    emitter.on(
      BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED,
      this.onReconnectCompleted
    );
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'conversation.unread_count_changed':
        this.onConversationUnreadCountChanged,
      'account.cache_invalidated': this.onCacheInvalidate,
      'kanban.changed': this.onKanbanChanged,
      'account.enrichment_completed': this.onEnrichmentCompleted,
      'copilot.message.created': this.onCopilotMessageCreated,
      'voice_call.incoming': this.onVoiceCallIncoming,
      'voice_call.accepted': this.onVoiceCallAccepted,
      'voice_call.outbound_connected': this.onVoiceCallOutboundConnected,
      'voice_call.outbound_accepted': this.onVoiceCallOutboundAccepted,
      'voice_call.ended': this.onVoiceCallEnded,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  onReconnectCompleted = async inboxChanges => {
    if (
      inboxChanges?.accountId !== this.accountId ||
      !this.isCurrentAccount()
    ) {
      return;
    }
    const {
      revokedInboxIds = [],
      grantedInboxIds = [],
      authorizedInboxIds = [],
      authorizationUnknown = true,
    } = inboxChanges || {};
    revokedInboxIds.forEach(id => this.revokedInboxIds.add(Number(id)));
    grantedInboxIds.forEach(id => this.revokedInboxIds.delete(Number(id)));
    authorizedInboxIds.forEach(id => this.revokedInboxIds.delete(Number(id)));
    await this.revalidateConversationAccess(revokedInboxIds, {
      refreshList:
        authorizationUnknown || Boolean(inboxChanges?.inboxAccessChanged),
      failClosed: authorizationUnknown,
    });
  };

  disconnect() {
    emitter.off(
      BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED,
      this.onReconnectCompleted
    );
    const store = this.app.$store;
    store.dispatch('invalidateConversationAccessRequests');
    store.dispatch('contactConversations/clear');
    store.dispatch('notifications/clear');
    super.disconnect();
  }

  isCurrentAccount = () =>
    !this.isDisposed &&
    this.app.$store.getters.getCurrentAccountId === this.accountId;

  getCurrentConversationRouteId = () => {
    const params = this.app.$router?.currentRoute?.value?.params || {};
    return getConversationIdFromRouteParams(params);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onKanbanChanged = data => {
    emitter.emit(BUS_EVENTS.KANBAN_CHANGED, data);
  };

  isAValidEvent = data => {
    const inboxId =
      data.inbox_id ||
      data.conversation?.inbox_id ||
      data.notification?.primary_actor?.inbox_id;
    return (
      this.app.$store.getters.getCurrentAccountId === data.account_id &&
      !this.revokedInboxIds.has(Number(inboxId))
    );
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUnreadCountChanged = () => {
    this.refreshConversationUnreadCountsWithFilteredRetry();
  };

  refreshConversationUnreadCountsWithFilteredRetry = () => {
    this.throttledFetchConversationUnreadCounts();
    this.scheduleFilteredUnreadCountsRetry();
  };

  throttledFetchConversationUnreadCounts = () => {
    const now = Date.now();
    const elapsedTime = now - this.lastUnreadCountsFetchAt;

    if (
      this.lastUnreadCountsFetchAt === null ||
      elapsedTime >= UNREAD_COUNTS_REFETCH_THROTTLE_MS
    ) {
      this.clearUnreadCountsFetchTimer();
      this.fetchConversationUnreadCounts();
      return;
    }

    if (this.unreadCountsFetchTimer) return;

    this.unreadCountsFetchTimer = setTimeout(() => {
      this.unreadCountsFetchTimer = null;
      this.fetchConversationUnreadCounts();
    }, UNREAD_COUNTS_REFETCH_THROTTLE_MS - elapsedTime);
  };

  clearUnreadCountsFetchTimer = () => {
    if (!this.unreadCountsFetchTimer) return;

    clearTimeout(this.unreadCountsFetchTimer);
    this.unreadCountsFetchTimer = null;
  };

  scheduleMentionUnreadCountsFetch = () => {
    if (!this.isFilteredUnreadCountsEnabled()) return;

    // Mention invalidation runs through the async dispatcher, and stale snapshots
    // can be served until the filtered-count backend refresh window opens.
    this.scheduleUnreadCountsFetchAfter(
      'mentionUnreadCountsFetchTimer',
      MENTION_UNREAD_COUNTS_REFETCH_DELAY_MS
    );
    this.scheduleUnreadCountsFetchAfter(
      'mentionUnreadCountsRetryTimer',
      getFilteredUnreadCountsRefreshRetryDelay(),
      { reset: true }
    );
  };

  scheduleFilteredUnreadCountsRetry = () => {
    if (!this.isFilteredUnreadCountsEnabled()) return;

    // Filtered snapshots can intentionally stay stale until the backend
    // refresh window opens.
    this.scheduleUnreadCountsFetchAfter(
      'filteredUnreadCountsRetryTimer',
      getFilteredUnreadCountsRefreshRetryDelay(),
      { reset: true }
    );
  };

  scheduleUnreadCountsFetchAfter = (
    timerName,
    delay,
    { reset = false } = {}
  ) => {
    if (this[timerName]) {
      if (!reset) return;

      clearTimeout(this[timerName]);
    }

    this[timerName] = setTimeout(() => {
      this[timerName] = null;
      this.throttledFetchConversationUnreadCounts();
    }, delay);
  };

  fetchConversationUnreadCounts = () => {
    if (!this.isConversationUnreadCountsEnabled()) return;

    this.lastUnreadCountsFetchAt = Date.now();
    this.app.$store.dispatch('conversationUnreadCounts/get');
  };

  isConversationUnreadCountsEnabled = () => {
    const accountId = this.app.$store.getters.getCurrentAccountId;
    const isFeatureEnabled =
      this.app.$store.getters['accounts/isFeatureEnabledonAccount'];

    return isFeatureEnabled?.(
      accountId,
      FEATURE_FLAGS.CONVERSATION_UNREAD_COUNTS
    );
  };

  isFilteredUnreadCountsEnabled = () => {
    const accountId = this.app.$store.getters.getCurrentAccountId;
    const isFeatureEnabled =
      this.app.$store.getters['accounts/isFeatureEnabledonAccount'];

    return (
      isFeatureEnabled?.(accountId, FEATURE_FLAGS.CONVERSATION_UNREAD_COUNTS) &&
      isFeatureEnabled?.(accountId, FEATURE_FLAGS.UNREAD_COUNT_FOR_FILTERS)
    );
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
    this.scheduleMentionUnreadCountsFetch();
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  onCopilotMessageCreated = data => {
    this.app.$store.dispatch('copilotMessages/upsert', data);
  };

  onEnrichmentCompleted = () => {
    this.app.$store.dispatch('accounts/get', { silent: true });
  };

  onCacheInvalidate = async data => {
    if (!this.isCurrentAccount()) return;
    if (data.granted_inbox_id) {
      this.revokedInboxIds.delete(Number(data.granted_inbox_id));
    }
    if (data.revoked_inbox_id) {
      this.revokedInboxIds.add(Number(data.revoked_inbox_id));
    }

    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    const inboxRefresh = this.app.$store.dispatch('inboxes/revalidate', {
      newKey: keys.inbox,
      force: Boolean(data.access_changed),
    });
    const selectedConversationAccessChanged =
      data.conversation_id &&
      Number(data.conversation_id) ===
        Number(this.app.$store.getters.getSelectedChat?.id);
    const immediateAccessRefresh =
      data.access_changed && !data.granted_inbox_id
        ? this.revalidateConversationAccess(data.revoked_inbox_id, {
            failClosed: Boolean(selectedConversationAccessChanged),
          })
        : null;
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
    this.app.$store.dispatch('revalidateCannedResponses', {
      newKey: keys.canned_response,
    });

    if (data.access_changed) {
      const [refreshedInboxes] = await Promise.all([
        inboxRefresh,
        immediateAccessRefresh,
      ]);
      if (!this.isCurrentAccount()) return;
      if (Array.isArray(refreshedInboxes)) {
        const revokedInboxId = Number(data.revoked_inbox_id);
        refreshedInboxes.forEach(({ id }) => {
          const inboxId = Number(id);
          if (inboxId !== revokedInboxId) this.revokedInboxIds.delete(inboxId);
        });
      }
      if (!immediateAccessRefresh) {
        await this.revalidateConversationAccess(data.revoked_inbox_id);
      }
      if (!this.isCurrentAccount()) return;
    }

    if (this.isFilteredUnreadCountsEnabled()) {
      // Inbox/team/label visibility changes can change the accessible set used
      // by filtered unread counts even when no conversation row changes.
      this.refreshConversationUnreadCountsWithFilteredRetry();
    }
  };

  refreshConversationListForAccess = async preserveConversationId => {
    if (!this.isCurrentAccount()) return false;
    const store = this.app.$store;
    const query = getConversationListQuery(store.getters);
    if (query) {
      try {
        await store.dispatch('fetchFilteredConversations', {
          queryData: query,
          page: 1,
          replace: true,
          preserveConversationId,
        });
        return true;
      } catch (error) {
        return false;
      }
    }
    await store.dispatch('updateChatListFilters', {
      page: 1,
      updatedWithin: null,
    });
    if (!this.isCurrentAccount()) return false;
    return store.dispatch('fetchAllConversations', {
      replace: true,
      preserveConversationId,
    });
  };

  revalidateConversationAccess = async (
    revokedInboxId,
    { refreshList = true, failClosed = false } = {}
  ) => {
    if (!this.isCurrentAccount()) return;
    const store = this.app.$store;
    const selectedChat = store.getters.getSelectedChat;
    const selectedRouteId = this.getCurrentConversationRouteId();
    const revokedInboxIds = (
      Array.isArray(revokedInboxId) ? revokedInboxId : [revokedInboxId]
    ).filter(Boolean);
    store.dispatch('invalidateConversationAccessRequests');
    store.dispatch('contactConversations/clear');
    store.dispatch('notifications/clear');
    const notificationFilters =
      store.getters['notifications/getNotificationFilters'] || {};
    const notificationsRequest = store.dispatch('notifications/index', {
      ...notificationFilters,
      page: 1,
    });
    if (failClosed) {
      store.dispatch('purgeConversationAccessCache');
    }
    revokedInboxIds.forEach(id =>
      store.dispatch('removeConversationsByInbox', Number(id))
    );

    const authorizedConversation = selectedChat?.id
      ? await store.dispatch('revalidateConversationAccess', selectedChat.id)
      : null;
    if (!this.isCurrentAccount()) return;
    const selectedAfterAuthorization = store.getters.getSelectedChat;
    const selectionChanged = Boolean(
      this.getCurrentConversationRouteId() !== selectedRouteId ||
        (selectedAfterAuthorization?.id &&
          selectedAfterAuthorization.id !== selectedChat?.id)
    );
    const isAuthorized = Boolean(authorizedConversation);
    const selectedInboxWasRevoked = revokedInboxIds
      .map(Number)
      .includes(Number(selectedChat?.inbox_id));
    let selectedConversationRestored = false;
    if (
      (failClosed || selectedInboxWasRevoked) &&
      authorizedConversation &&
      !selectionChanged
    ) {
      await store.dispatch('setActiveChat', { data: authorizedConversation });
      selectedConversationRestored = true;
    }
    if (selectedInboxWasRevoked && !isAuthorized) {
      await store.dispatch('removeConversationCache', selectedChat.id);
    }
    if (!this.isCurrentAccount()) return;
    const currentSelectedChat = store.getters.getSelectedChat;
    if (refreshList) {
      const currentSelectionChanged = Boolean(
        this.getCurrentConversationRouteId() !== selectedRouteId ||
          (currentSelectedChat?.id &&
            currentSelectedChat.id !== selectedChat?.id)
      );
      const preserveConversationId = currentSelectionChanged
        ? currentSelectedChat.id
        : isAuthorized && selectedChat.id;
      await this.refreshConversationListForAccess(
        preserveConversationId || undefined
      );
    }
    if (!this.isCurrentAccount()) return;
    await notificationsRequest;
    if (!this.isCurrentAccount()) return;
    const finalSelectedChat = store.getters.getSelectedChat;
    if (
      isAuthorized &&
      !selectedConversationRestored &&
      this.getCurrentConversationRouteId() === selectedRouteId &&
      finalSelectedChat?.id === selectedChat.id &&
      finalSelectedChat.dataFetched === undefined
    ) {
      await store.dispatch('setActiveChat', { data: finalSelectedChat });
    }
    if (
      isAuthorized &&
      finalSelectedChat?.id === selectedChat.id &&
      selectedChat.meta?.sender?.id
    ) {
      store.dispatch('contactConversations/get', selectedChat.meta.sender.id);
    } else if (isAuthorized === false) {
      this.fetchConversationStats();
    }
  };

  onVoiceCallIncoming = data => {
    if (data?.provider !== VOICE_CALL_PROVIDERS.WHATSAPP) return;
    // Defense in depth: the server already filters to online agent streams,
    // but if anything ever broadcasts to a broader stream (e.g. account-wide),
    // an agent who's set availability=offline/busy shouldn't ring.
    const availability = this.app.$store.getters.getCurrentUserAvailability;
    if (availability !== 'online') return;

    useCallsStore().addCall({
      callSid: data.call_id,
      callId: data.id,
      conversationId: data.conversation_id,
      inboxId: data.inbox_id,
      callDirection: VOICE_CALL_DIRECTION.INBOUND,
      provider: VOICE_CALL_PROVIDERS.WHATSAPP,
      sdpOffer: data.sdp_offer,
      iceServers: data.ice_servers,
      caller: data.caller,
    });
  };

  // Inbound call accepted (in this tab or a sibling tab/window on the same
  // account, on either provider). Broadcast is account-wide, so drop the
  // ringing card everywhere except the tab that actually owns the now-active
  // call — removing an active call here would tear down its live WebRTC
  // session. Check isLocalWhatsappCall/isLocalCall (both set synchronously
  // before their respective accept/join API calls) rather than the store's
  // isActive flag, which this tab may not have set yet.
  // Mark dismissed regardless of locality: the ringing message.created for
  // this call is queued through ActionCableBroadcastJob and can still be
  // delivered after this (synchronous) broadcast, which would otherwise
  // re-add the call as ringing once it finally arrives.
  // eslint-disable-next-line class-methods-use-this
  onVoiceCallAccepted = data => {
    if (!data?.provider) return;
    markCallDismissed(data.call_id);
    const isLocal =
      data.provider === VOICE_CALL_PROVIDERS.WHATSAPP
        ? isLocalWhatsappCall(data.id)
        : isLocalCall(data.call_id);
    if (isLocal) return;
    useCallsStore().removeCall(data.call_id);
  };

  // `connect` is the WebRTC tunnel-ready signal (fires ~20s before pickup
  // for outbound). Apply the SDP answer so the handshake completes during
  // ringing, but stay non-active until `outbound_accepted` arrives.
  // eslint-disable-next-line class-methods-use-this
  onVoiceCallOutboundConnected = async data => {
    if (data?.provider !== VOICE_CALL_PROVIDERS.WHATSAPP || !data.sdp_answer)
      return;
    // Account-wide broadcast that can arrive before /initiate sets this tab's
    // call id. applyOutboundAnswer filters foreign calls and buffers the answer
    // until the id is known, so we must not drop it here on a null activeCallId.
    try {
      await applyOutboundAnswer(data.id, data.sdp_answer);
    } catch (_) {
      /* noop */
    }
  };

  // Real pickup signal — Meta sends status=ACCEPTED on the call when the
  // contact answers. Flip active (timer starts) and arm the recorder.
  // eslint-disable-next-line class-methods-use-this
  onVoiceCallOutboundAccepted = data => {
    if (data?.provider !== VOICE_CALL_PROVIDERS.WHATSAPP) return;
    const store = useCallsStore();
    if (!store.calls.some(c => c.callSid === data.call_id)) return;
    store.setCallActive(data.call_id);
    armOutboundRecorder();
  };

  // eslint-disable-next-line class-methods-use-this
  onVoiceCallEnded = async data => {
    if (!Object.values(VOICE_CALL_PROVIDERS).includes(data?.provider)) return;
    // A still-queued ringing message.created (see onVoiceCallAccepted) must not
    // resurrect a call that has already ended.
    markCallDismissed(data.call_id);
    // The store entry should always be removed for this account-wide broadcast,
    // but the WebRTC/recorder teardown must only run for the call this tab owns
    // — otherwise an unrelated agent's call ending would stop this tab's
    // recorder and upload its chunks against the wrong call id. For Twilio,
    // removeCall's own isActive check already scopes teardown to the owning
    // tab (via TwilioVoiceClient.endClientCall in teardownByProvider), so no
    // extra guard is needed here — only WhatsApp's recorder upload must be
    // awaited before the store entry (and its in-memory chunks) is wiped.
    if (
      data.provider === VOICE_CALL_PROVIDERS.WHATSAPP &&
      isLocalWhatsappCall(data.id)
    ) {
      // Await upload before removeCall — the store's sync teardown would otherwise
      // wipe the recorder chunks before they reach the server.
      try {
        await handleWhatsappRemoteEnd(data.id);
      } catch (_) {
        /* noop */
      }
    }
    useCallsStore().removeCall(data.call_id);
  };
}

export default {
  init(store, pubsubToken, router) {
    return new ActionCableConnector(
      { $store: store, $router: router },
      pubsubToken
    );
  },
};
